import {
  datetime,
  type DatetimeType,
  type Moment,
} from '@common/services/datetime';
// @TODO(WORK-5621): humanizeTimezone keeps moment-timezone for the `z` timezone
// abbreviation (dayjs renders GMT±H — accepted divergence WORKS-33B). Drop this
// import when moment is removed from the dependency tree.
import momentTimezone from 'moment-timezone';

// Everything datetime() accepts, plus a Moment object: some callers still pass
// shadow moments during the Moment → dayjs migration. datetime() handles a Moment
// at runtime (createDatetime's moment-object branch), but its dayjs-derived
// signature does not, so `toDatetime` casts the Moment away at the single call
// boundary rather than lying about every public parameter.
type DateInput =
  | DatetimeType
  | Moment
  | string
  | number
  | Date
  | null
  | undefined;

const toDatetime = (date: DateInput): DatetimeType =>
  datetime(date as Exclude<DateInput, Moment>);

export const now = (): DatetimeType => datetime();

export const US_DATE_FORMAT = 'MM/DD/YYYY';

const ISO_DATE_FORMAT = 'YYYY-MM-DD';
const US_COMPACT_DATE_FORMAT = 'M/D/YYYY';
const US_COMPACT_DATE_FORMAT_WITH_TIME_AND_TZ = 'M/D/YY, h:mmA z';
const US_HUMAN_DATE_TIME_WITH_TZ = 'MM/DD/YYYY [at] h:mma z';
export const HUMAN_TIME_FORMAT = 'HH:mm';

export const toUSCompactDateFormat = (date: DateInput): string =>
  toDatetime(date).format(US_COMPACT_DATE_FORMAT);

export const toUSCompactDateFormatWithTimeAndTZ = (date: DateInput): string =>
  toDatetime(date).format(US_COMPACT_DATE_FORMAT_WITH_TIME_AND_TZ);

export const toISODateFormat = (date: DateInput): string =>
  toDatetime(date).format(ISO_DATE_FORMAT);
export const fromISODateFormat = (dateString: string): DatetimeType =>
  datetime(dateString, ISO_DATE_FORMAT, true);

export const toUSFormat = (date: DateInput): string =>
  toDatetime(date).format(US_DATE_FORMAT);
export const fromUSFormat = (dateString: string): DatetimeType =>
  datetime(dateString, US_DATE_FORMAT, true);

// Renders in an explicit named timezone so the `z` token resolves to a real
// abbreviation (e.g. "EDT"); callers pass the viewing user's membership timezone
// (the same source TimeWithTimeZone uses), defaulting to UTC when unknown. The
// previous `zz` token produced an empty string under moment and a doubled offset
// under the shadow dayjs (WORK-6090, WORKS-331) — `z` matches across both.
export const toHumanDateTimeWithTZ = (
  date: DateInput,
  timeZone = 'UTC'
): string =>
  toDatetime(date).tz(timeZone).format(US_HUMAN_DATE_TIME_WITH_TZ).trim();

export const toHumanTimeFormat = (date: DateInput): string =>
  toDatetime(date).format(HUMAN_TIME_FORMAT);

export const ifValidOrNow = (date: DateInput): DatetimeType => {
  const datetimeDate = toDatetime(date);
  return datetimeDate.isValid() ? datetimeDate : now();
};

// TODO: remove toYearMonthDay, and rename its usages to toISODateFormat
export const toYearMonthDay = toISODateFormat;

export const formatToYearMonthDay = (date: DateInput): string | undefined => {
  if (!date) return;

  return toDatetime(date).format(ISO_DATE_FORMAT);
};

export const months = [
  { name: 'January', maxDays: 31 },
  { name: 'February', maxDays: 28 },
  { name: 'March', maxDays: 31 },
  { name: 'April', maxDays: 30 },
  { name: 'May', maxDays: 31 },
  { name: 'June', maxDays: 30 },
  { name: 'July', maxDays: 31 },
  { name: 'August', maxDays: 31 },
  { name: 'September', maxDays: 30 },
  { name: 'October', maxDays: 31 },
  { name: 'November', maxDays: 30 },
  { name: 'December', maxDays: 31 },
];

export function isPastDay(date: DateInput): boolean {
  const datePlusOneDay = toDatetime(date).add(1, 'day');
  return Boolean(date) && datePlusOneDay.isBefore(now());
}

export function humanizeTimezone(tz: string | null | undefined): string {
  if (!tz) return '';

  const offset = momentTimezone().tz(tz).format('z');
  const parsedTimezoneName = tz.split('/').join(' / ').replace(/_/g, ' ');

  return `${offset} - ${parsedTimezoneName}`;
}

export function humanizeHours(value: number): string {
  if (value < 24) {
    return humanizeWithPlural(value, 'hour');
  }

  const days = Math.floor(value / 24);
  const hours = value % 24;

  const humanizedDays = humanizeWithPlural(days, 'day');
  const humanizedHours = humanizeWithPlural(hours, 'hour');

  return hours === 0 ? humanizedDays : `${humanizedDays} and ${humanizedHours}`;
}

function humanizeWithPlural(value: number, name: string): string {
  return `${value} ${name}${value > 1 ? 's' : ''}`;
}

export function generateGreetings(): string {
  const currentHour = +datetime().format('HH');

  if (currentHour >= 3 && currentHour < 12) {
    return 'Good morning';
  }

  if (currentHour >= 12 && currentHour < 17) {
    return 'Good afternoon';
  }

  if (currentHour >= 17 && currentHour < 23) {
    return 'Good evening';
  }

  if (currentHour >= 23 || currentHour < 3) {
    return 'Good night';
  }

  return 'Hello';
}
